import React, { useState } from 'react';
import { useSelector } from 'react-redux';
import { useTheme } from '@emotion/react';
import { DeviceTypes } from '@gumtree/shared/src/types/client-data';
import { Button, Dialog, Icon } from '@gumtree/ui-library';
import { trackV2 } from '@gumtree/shared/src/util/track-ga-event';
import { excludePrimitives } from '@gumtree/shared/src/util/list-of-primitives';

import { CUSTOM_FILTERS, DEFAULT_FILTER_LABEL, NON_DEFAULT_FILTERS } from '../shared/constants';
import Make from './make';
import BooleanFilter from '../filters/boolean-filter';
import DefaultFilter from '../filters/default-filter';
import {
    FilterButtonContainer,
    filterButtonCss,
    SearchContainer,
    StyledContentContainer,
} from './index.style';
import ClearFiltersButton from './clear-filters-button';
import Search from '../search-button';
import CategoryAccordion from './category-accordion';
import type { SrpState } from '../../reducers';
import FrameAccordion from '../filters/frame/frame-accordion';
import { FilterItem } from '../../types';
import RangeFilterFrame from './range-filter-frame';
import { RangeFilterFreeformFrame } from './range-filter-freeform-frame';
import Categories from './categories';
import SearchableFilter from '../filters/searchable-filter';

export const FiltersOnGridView = ({
    device,
    className,
}: {
    device: DeviceTypes | undefined;
    className?: string;
}) => {
    const theme = useTheme();
    const {
        resultsPage: { fullscreenFilter, filtersDictionary, filtersOrder, currentFilterStates },
    }: SrpState = useSelector((data: SrpState) => data);

    const isCategoriesOpened = fullscreenFilter.isActive;

    const [isDialogOpened, setIsDialogOpened] = useState(false);

    const isDesktop = device === DeviceTypes.DESKTOP;

    const handleToggleDialog = () => {
        const eventName = isDialogOpened ? 'SRP-refine-modal-close' : 'SRP-refine-modal-open';
        trackV2(eventName);
        setIsDialogOpened((prev) => !prev);
    };

    const getAccordionLabel = (filterKey: string) => {
        if (filterKey === 'vehicle_average_mpg') {
            return 'Fuel consumption';
        }
        return filtersDictionary[filterKey].label;
    };

    const booleanFilterKeys = filtersOrder.filter(
        (filterKey) => filtersDictionary[filterKey]?.type === 'boolean'
    );
    const petsHealthFilterKeys = filtersOrder.filter(
        (filterKey) => filtersDictionary[filterKey]?.type === 'boolean-pets-health'
    );
    const remainingFilterKeys = excludePrimitives(filtersOrder, [
        ...booleanFilterKeys,
        ...petsHealthFilterKeys,
    ]);

    return (
        <FilterButtonContainer className={className} isDialogOpened={isDialogOpened}>
            <Button
                display="secondary"
                label="Filters"
                icon={<Icon type="refine" />}
                onClick={handleToggleDialog}
                css={filterButtonCss(theme)}
            />
            {isDialogOpened && (
                <>
                    <Dialog
                        isOpen={isDialogOpened}
                        onClose={handleToggleDialog}
                        title="Filters"
                        className="filter-dialog"
                        closeIconType="clear"
                        containerClassName={
                            isCategoriesOpened
                                ? 'dialog-container__filter-close'
                                : 'dialog-container__filter-open'
                        }
                        scrollStopper={false}
                    >
                        <StyledContentContainer>
                            {remainingFilterKeys.map((filterKey) => {
                                const values = filtersDictionary[filterKey]?.values || [];

                                if (filtersDictionary[filterKey]?.type === 'range_freeform') {
                                    return (
                                        <RangeFilterFreeformFrame
                                            filterKey={filterKey}
                                            filtersDictionary={filtersDictionary}
                                            currentFilterStates={currentFilterStates}
                                        />
                                    );
                                }

                                if (filtersDictionary[filterKey]?.type === 'enum_range') {
                                    return (
                                        <RangeFilterFrame
                                            currentFilterStates={currentFilterStates}
                                            filtersDictionary={filtersDictionary}
                                            filterKey={filterKey}
                                        />
                                    );
                                }

                                if (filterKey === CUSTOM_FILTERS.MAKE) {
                                    return <Make key="make-model" />;
                                }

                                if (filterKey === CUSTOM_FILTERS.MODEL) {
                                    return null;
                                }

                                if (
                                    NON_DEFAULT_FILTERS.includes(filterKey) ||
                                    (values.length < 2 && values[0]?.label === DEFAULT_FILTER_LABEL)
                                ) {
                                    return null;
                                }

                                const filterItemsRaw =
                                    filtersDictionary[filterKey]?.values || ([] as FilterItem[]);
                                const selectedFilterValue = currentFilterStates[filterKey];

                                if (filtersDictionary[filterKey]?.type === 'searchable-enum') {
                                    return (
                                        <FrameAccordion
                                            title={filtersDictionary[filterKey].label ?? ''}
                                        >
                                            <SearchableFilter
                                                filterKey={filterKey}
                                                filterItemsRaw={filterItemsRaw}
                                                selectedFilterValue={selectedFilterValue}
                                                isStateful
                                            />
                                        </FrameAccordion>
                                    );
                                }

                                return (
                                    <FrameAccordion
                                        key={filterKey}
                                        filterKey={filterKey}
                                        title={getAccordionLabel(filterKey)}
                                    >
                                        <DefaultFilter
                                            filterKey={filterKey}
                                            shortListLength={5}
                                            filterItemsRaw={filterItemsRaw}
                                            selectedFilterValue={selectedFilterValue}
                                            isStateful
                                        />
                                    </FrameAccordion>
                                );
                            })}

                            {booleanFilterKeys.length > 0 && (
                                <FrameAccordion title="Other options">
                                    <BooleanFilter
                                        currentFilterStates={currentFilterStates}
                                        filtersDictionary={filtersDictionary}
                                        filterKeys={booleanFilterKeys}
                                    />
                                </FrameAccordion>
                            )}

                            {petsHealthFilterKeys.length > 0 && (
                                <FrameAccordion title="Health and documentation">
                                    <BooleanFilter
                                        currentFilterStates={currentFilterStates}
                                        filtersDictionary={filtersDictionary}
                                        filterKeys={petsHealthFilterKeys}
                                    />
                                </FrameAccordion>
                            )}
                            <CategoryAccordion />
                        </StyledContentContainer>

                        {!isDesktop && <ClearFiltersButton isFillWidth />}

                        <SearchContainer>
                            {isDesktop && <ClearFiltersButton />}
                            <Search />
                        </SearchContainer>
                        <Categories />
                    </Dialog>
                </>
            )}
        </FilterButtonContainer>
    );
};
